import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useLogo } from '../../contexts/LogoContext';

/**
 * GlobalLoadingScreen - A reusable loading screen component with consistent styling
 * 
 * Features:
 * - Theme integration with dark/light mode support
 * - Logo display capability
 * - Smooth fade transitions
 * - Accessibility features
 * - Consistent styling with existing design system
 */
const GlobalLoadingScreen = ({ 
  message = 'Loading...', 
  showLogo = true,
  className = ''
}) => {
  const { isDarkMode, primaryColor } = useTheme();
  const { getDisplayLogo } = useLogo();
  const displayLogo = getDisplayLogo(isDarkMode);

  return (
    <div 
      className={`fixed inset-0 z-50 flex flex-col items-center justify-center min-h-screen animate-fade-in ${className}`}
      style={{
        backgroundColor: isDarkMode ? '#0F172A' : '#FFFFFF',
        color: isDarkMode ? '#E4E6EB' : '#1F1F1F'
      }}
      role="status"
      aria-live="polite"
      aria-label={message}
    >
      {/* Logo Section */}
      {showLogo && displayLogo && (
        <div className="mb-8 animate-scale-in">
          <img
            src={displayLogo}
            alt="Application Logo"
            className="h-16 w-auto object-contain"
          />
        </div>
      )}

      {/* Loading Spinner */}
      <div className="relative mb-6">
        <div 
          className="w-12 h-12 border-4 border-solid rounded-full animate-spin"
          style={{
            borderColor: `${primaryColor}33`, // 20% opacity
            borderTopColor: primaryColor
          }}
          aria-hidden="true"
        />
        
        {/* Inner spinner for enhanced visual effect */}
        <div 
          className="absolute inset-2 w-8 h-8 border-2 border-solid rounded-full animate-spin"
          style={{
            borderColor: 'transparent',
            borderTopColor: `${primaryColor}66`, // 40% opacity
            animationDirection: 'reverse',
            animationDuration: '1.5s'
          }}
          aria-hidden="true"
        />
      </div>

      {/* Loading Message */}
      <div className="text-center animate-fade-in animate-stagger-1">
        <p 
          className="text-lg font-medium mb-2"
          style={{ color: isDarkMode ? '#E4E6EB' : '#1F1F1F' }}
        >
          {message}
        </p>
        
        {/* Subtle loading dots animation */}
        <div className="flex justify-center space-x-1">
          {[0, 1, 2].map((index) => (
            <div
              key={index}
              className="w-2 h-2 rounded-full animate-pulse-subtle"
              style={{
                backgroundColor: `${primaryColor}80`, // 50% opacity
                animationDelay: `${index * 0.2}s`
              }}
              aria-hidden="true"
            />
          ))}
        </div>
      </div>

      {/* Screen reader only text for accessibility */}
      <div className="sr-only">
        Please wait while the application loads. This may take a few moments.
      </div>
    </div>
  );
};

export default GlobalLoadingScreen;